import { Injectable, computed, effect, inject, signal } from "@angular/core";
import { randomUUID, DEFAULT_AGENT_ID } from "@copilotkit/shared";
import { AgentsApiPrivateService } from "./agents-api-private.service";
import type { ThreadInfo, RawLangGraphMessage } from "../model/thread.types";
import type { Message } from "@ag-ui/client";
import { AgentFactory, injectAgentStore } from "../../../agent-ui";

/**
 * Coordinator for the shared conversation session used by bubble, toolbar, and AI full-screen.
 *
 * Owns ONLY the active thread and agent selection — NOT message storage.
 * Messages live in AgentFactory (one AgentStore per agentId, root-scoped, persistent).
 *
 * All entry points that share a session read the same `threadId` and `agentId` signals directly,
 * so Angular's OnPush signal tracking is unambiguous.
 *
 * Independent panels (property analyst, etc.) provide this service at the component level —
 * Angular DI gives them a private instance so their session never interferes with the root one.
 */
@Injectable({ providedIn: "root" })
export class AiChatSessionService {
  private readonly agentsApi = inject(AgentsApiPrivateService);

  private readonly agentFactory = inject(AgentFactory);

  /** Active conversation thread. Changing this value starts a new conversation everywhere. */
  readonly threadId = signal<string>(randomUUID());

  /** Currently selected LangGraph agent ID. */
  readonly agentId = signal<string | null>(null);

  /** Thread history loaded from the Agents API. */
  readonly threads = signal<ThreadInfo[]>([]);

  /** True while a thread list request is in flight. */
  readonly isLoadingThreads = signal(false);

  /**
   * Threads whose first run has already triggered a post-run refetch, so we
   * don't spam /threads/search on every subsequent turn of the same thread.
   */
  private readonly refetchedAfterFirstRun = new Set<string>();

  /**
   * Last active threadId per agentId. Lets switchAgent() restore the conversation an agent
   * was left on instead of always starting a new one — each agent keeps its own thread.
   */
  private readonly agentThreads = new Map<string, string>();

  /** Backing signal for the currently active agent store (via injectAgentStore). */
  private readonly activeAgentStore = injectAgentStore(computed(() => this.agentId() ?? undefined));

  constructor() {
    // When the active agent finishes a run on a brand-new thread, the backend's
    // thread_titler node has just written `metadata.thread_name` — refetch the
    // thread list so the generated title replaces the fallback date label in the UI.
    let previousRunning = false;
    effect(() => {
      const running = this.activeAgentStore().isRunning();
      const currentThreadId = this.threadId();
      if (previousRunning && !running && currentThreadId && !this.refetchedAfterFirstRun.has(currentThreadId)) {
        this.refetchedAfterFirstRun.add(currentThreadId);
        this.loadThreads();
      }
      previousRunning = running;
    });
  }

  /** Generate a fresh thread UUID, resetting the conversation in all shared entry points. */
  startNewChat(): void {
    this.beginFreshThread(this.agentId() ?? DEFAULT_AGENT_ID);
  }

  /**
   * Switches the active agent, restoring the conversation it was last left on.
   *
   * Each agent is a separate LangGraph graph with its own checkpoint history, so it needs its
   * own threadId — reusing one agent's thread for another leaves the LangGraph thread holding
   * messages the new agent's local AgentStore doesn't have, which the backend misreads as a
   * "regenerate from an earlier checkpoint" request and fails with "Message not found" (the new
   * message was never part of that thread's history).
   *
   * agentThreads remembers the last threadId used per agent, so switching back to an
   * already-visited agent resumes it instead of starting over; a never-visited agent still
   * gets a brand-new thread.
   */
  switchAgent(agentId: string): void {
    const previousAgentId = this.agentId();
    if (previousAgentId === agentId) return;
    if (previousAgentId) this.agentThreads.set(previousAgentId, this.threadId());

    this.agentId.set(agentId);

    const savedThreadId = this.agentThreads.get(agentId);
    if (savedThreadId) {
      this.threadId.set(savedThreadId);
    } else {
      this.beginFreshThread(agentId);
    }
  }

  /** Clears the given agent's messages, assigns it a brand-new thread, and remembers it. */
  private beginFreshThread(agentId: string): void {
    this.agentFactory.clearMessages(agentId);
    const newThreadId = randomUUID();
    this.threadId.set(newThreadId);
    this.agentThreads.set(agentId, newThreadId);
  }

  /**
   * Loads the thread list for the currently selected agent via POST /threads/search
   * filtered by metadata.graph_id, which matches the agent's LangGraph graph name.
   * Results are sorted newest-first.
   */
  loadThreads(): void {
    const agentId = this.agentId();
    if (!agentId) {
      console.error("AiChatSessionService: loadThreads called without an agentId");
      return;
    }
    if (this.isLoadingThreads()) return;
    this.isLoadingThreads.set(true);
    const metadata: Record<string, string> = { graph_id: agentId };
    this.agentsApi.searchThreads(metadata).subscribe({
      next: (threads) => {
        const sorted = [...threads].sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
        this.threads.set(sorted);
        // Any thread returned by the server already has whatever title it will get —
        // no need to re-fetch after subsequent turns of these threads.
        for (const t of sorted) this.refetchedAfterFirstRun.add(t.threadId);
        this.isLoadingThreads.set(false);
      },
      error: () => {
        this.isLoadingThreads.set(false);
      },
    });
  }

  /**
   * Switches the active thread to an existing one.
   * Seeds the AgentStore BEFORE changing threadId so the newly-mounted lib-chat-container
   * renders the full conversation history without any empty-state flash.
   */
  switchThread(threadId: string): void {
    if (this.threadId() === threadId) return;
    this.agentsApi.getThreadState(threadId).subscribe({
      next: (raw) => {
        const rawMessages: RawLangGraphMessage[] = Array.isArray(raw.values?.messages)
          ? (raw.values.messages as RawLangGraphMessage[])
          : [];
        const messages = this.mapLangGraphMessages(rawMessages);
        const agentId = this.agentId() ?? DEFAULT_AGENT_ID;
        this.agentFactory.seedForThread(agentId, messages, threadId, raw.values);
        this.threadId.set(threadId);
        this.agentThreads.set(agentId, threadId);
      },
      error: () => {
        this.threadId.set(threadId);
        this.agentThreads.set(this.agentId() ?? DEFAULT_AGENT_ID, threadId);
      },
    });
  }

  private mapLangGraphMessages(rawMessages: RawLangGraphMessage[]): Message[] {
    const mapped: Message[] = [];
    for (const msg of rawMessages) {
      if (msg.type === "system") continue;
      const content = typeof msg.content === "string" ? msg.content : msg.content.map((c) => c.text ?? "").join("");
      if (msg.type === "human") {
        mapped.push({ id: msg.id, role: "user", content } as Message);
      } else if (msg.type === "ai") {
        mapped.push({ id: msg.id, role: "assistant", content } as Message);
      } else if (msg.type === "tool") {
        // Tool result messages must be included so prepareRegenerateStream can find
        // the correct LangGraph checkpoint by the last message ID.
        mapped.push({ id: msg.id, role: "tool", content } as unknown as Message);
      }
    }
    return mapped;
  }

  /**
   * Fetches the latest state for the given thread and re-seeds the AgentStore messages.
   * Used after a background job completes to pick up the backend-injected AIMessage
   * without changing the active thread.
   */
  refreshThread(agentId: string, threadId: string): void {
    this.agentsApi.getThreadState(threadId).subscribe({
      next: (raw) => {
        const rawMessages: RawLangGraphMessage[] = Array.isArray(raw.values?.messages)
          ? (raw.values.messages as RawLangGraphMessage[])
          : [];
        const messages = this.mapLangGraphMessages(rawMessages);
        this.agentFactory.seedForThread(agentId, messages, threadId, raw.values);
      },
      error: (err) => console.error("AiChatSessionService: refreshThread failed", err),
    });
  }

  /**
   * Deletes a thread via the Agents API and removes it from the local list.
   * If the deleted thread is the active one, starts a new chat.
   */
  deleteThread(threadId: string): void {
    this.agentsApi.deleteThread(threadId).subscribe({
      next: () => {
        this.threads.update((list) => list.filter((t) => t.threadId !== threadId));
        if (this.threadId() === threadId) {
          this.startNewChat();
        }
      },
      error: (err) => {
        console.error("AiChatSessionService: failed to delete thread", err);
      },
    });
  }
}
